import { z } from 'zod'
import { uuidSchema } from './common'

// Document status enum
export const documentStatusSchema = z.enum(['PROCESSING', 'INDEXED', 'FAILED', 'UPLOADED', 'PROCESSING_FAILED'])

// Document file type enum (only supported types)
export const documentFileTypeSchema = z.enum(['pdf', 'docx', 'txt', 'pptx'])

// Document category enum for better validation
export const documentCategorySchema = z.enum(['Policies', 'Benefits', 'Training', 'Forms', 'Legal', 'Procedures', 'Other'])

// Export constants for frontend use
export const DOCUMENT_CATEGORIES = documentCategorySchema.options
export const DOCUMENT_FILE_TYPES = documentFileTypeSchema.options
export const DOCUMENT_STATUSES = documentStatusSchema.options

// File extensions mapping for frontend validation
export const SUPPORTED_FILE_EXTENSIONS = ['.pdf', '.docx', '.txt', '.pptx']
export const FILE_TYPE_EXTENSIONS: Record<string, string[]> = {
  'pdf': ['.pdf'],
  'docx': ['.docx'],
  'txt': ['.txt'],
  'pptx': ['.pptx']
}

// Standard document metadata schema
export const documentMetadataSchema = z.object({
  title: z.string(), // Display title (duplicate of document.title for Pinecone convenience)
  description: z.string().optional(), // Brief description
  category: documentCategorySchema.optional(), // Document category
  tags: z.array(z.string()).optional(), // Additional tags for filtering
  department: z.string().optional(), // Relevant department
  version: z.string().optional(), // Document version
  expiryDate: z.string().datetime().optional(), // When document expires
  confidential: z.boolean().optional() // Whether document is confidential
}).strict()

// Document schemas
export const documentSchema = z.object({
  id: uuidSchema,
  title: z.string().max(255),
  filename: z.string().max(255),
  filePath: z.string(),
  fileType: documentFileTypeSchema,
  fileSize: z.number().int().min(1).max(50 * 1024 * 1024), // 50MB max
  uploadDate: z.date(),
  uploadedBy: uuidSchema.nullable(),
  status: documentStatusSchema,
  metadata: documentMetadataSchema.nullable()
})

export const uploadDocumentSchema = z.object({
  title: z.string().max(255),
  filename: z.string().max(255),
  fileType: documentFileTypeSchema,
  fileSize: z.number().int().min(1).max(50 * 1024 * 1024), // 50MB max
  metadata: documentMetadataSchema.optional()
})

export const updateDocumentSchema = z.object({
  filename: z.string().max(255).optional(),
  status: documentStatusSchema.optional(),
  metadata: documentMetadataSchema.optional()
})

export const documentQuerySchema = z.object({
  status: documentStatusSchema.optional(),
  fileType: documentFileTypeSchema.optional(),
  uploadedBy: uuidSchema.optional(),
  search: z.string().optional(), // Search in filename, title, description
  category: documentCategorySchema.optional(), // Filter by category
  department: z.string().optional(), // Filter by department
  confidential: z.union([z.boolean(), z.string()]).optional().transform(val => {
    if (typeof val === 'string') {
      return val.toLowerCase() === 'true'
    }
    return val
  }), // Filter confidential/public docs
  tags: z.string().optional(), // Comma-separated tags to filter by
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  hasExpiry: z.union([z.boolean(), z.string()]).optional().transform(val => {
    if (typeof val === 'string') {
      return val.toLowerCase() === 'true'
    }
    return val
  }), // Filter docs with/without expiry date
  isExpired: z.union([z.boolean(), z.string()]).optional().transform(val => {
    if (typeof val === 'string') {
      return val.toLowerCase() === 'true'
    }
    return val
  }) // Filter expired documents
})

export const documentSearchSchema = z.object({
  query: z.string().min(1, 'Search query is required'),
  limit: z.number().int().min(1).max(50).default(10),
  threshold: z.number().min(0).max(1).default(0.7)
})

// Type exports
export type Document = z.infer<typeof documentSchema>
export type UploadDocument = z.infer<typeof uploadDocumentSchema>
export type UpdateDocument = z.infer<typeof updateDocumentSchema>
export type DocumentQuery = z.infer<typeof documentQuerySchema>
export type DocumentSearch = z.infer<typeof documentSearchSchema>
export type DocumentStatus = z.infer<typeof documentStatusSchema>
export type DocumentFileType = z.infer<typeof documentFileTypeSchema>
export type DocumentCategory = z.infer<typeof documentCategorySchema>
export type DocumentMetadata = z.infer<typeof documentMetadataSchema> 